import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../services/api';

// Auth context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const validateToken = useCallback(async (token) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authApi.validate(token);
      
      if (response.valid && response.user) {
        setUser(response.user);
        sessionStorage.setItem('auth_token', token);
        return true;
      } else {
        setError(response.message || 'Invalid token');
        sessionStorage.removeItem('auth_token');
        return false;
      }
    } catch (err) {
      console.error('Token validation error:', err);
      setError(err.message || 'Failed to validate token');
      sessionStorage.removeItem('auth_token');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // Check for token in URL params (from external app redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (token) {
        await validateToken(token);
        // Clean up URL - remove token but keep other params
        urlParams.delete('token');
        const newUrl = urlParams.toString() 
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      } else {
        // Check for stored token
        const storedToken = sessionStorage.getItem('auth_token');
        if (storedToken) {
          await validateToken(storedToken);
        } else {
          setLoading(false);
        }
      }
    };
    
    initAuth();
  }, [validateToken]);

  const logout = useCallback(() => {
    setUser(null);
    setError(null);
    sessionStorage.removeItem('auth_token');
  }, []);

  const getAuthHeader = useCallback(() => {
    const token = sessionStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const getToken = useCallback(() => {
    return sessionStorage.getItem('auth_token');
  }, []);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      error, 
      isAdmin,
      logout, 
      getAuthHeader, 
      getToken,
      validateToken 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
